// src/components/home/HeroSection.tsx
import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { ChevronLeft, ChevronRight } from "lucide-react"

type HeroSectionProps = {
    banners?: any[]
}

export default function HeroSection({ banners = [] }: HeroSectionProps) {
    const [currentIndex, setCurrentIndex] = useState(0)

    // 4초마다 자동으로 다음 슬라이드로 넘어가는 로직
    useEffect(() => {
        if (banners.length <= 1) return;

        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % banners.length)
        }, 10000)

        return () => clearInterval(timer)
    }, [banners.length])

    const goToPrev = () => setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length)
    const goToNext = () => setCurrentIndex((prev) => (prev + 1) % banners.length)

    // 등록된 배너가 없을 경우 빈 화면 반환
    if (!banners || banners.length === 0) return null;

    return (
      // 전체 화면 너비를 사용하며 높이는 PC 기준 450px, 모바일 380px로 고정
      <div className="group relative w-full overflow-hidden bg-slate-50 h-[380px] lg:h-[450px]">
          <div
            className="flex h-full w-full transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
              {banners.map((banner, index) => (
                <div
                  key={banner.bannerId || index}
                  // 배너 이미지가 컨테이너 중앙에 오도록 정렬
                  className="relative h-full w-full shrink-0 flex items-center justify-center bg-slate-100"
                >
                    {/*
                            💡 핵심 수정 사항:
                            배경 이미지가 아닌 img 태그를 사용하여 원본 비율 유지.
                            object-contain을 사용해 이미지가 위아래로 잘리는 현상을 완벽히 방지합니다.
                        */}
                    <img
                      src={banner.imageUrl}
                      alt={banner.title || "메인 프로모션 배너"}
                      className="h-full w-full object-contain"
                    />

                    {/*
                            텍스트 오버레이 영역
                            (DB에 title, description, badgeText 값이 있을 때만 화면에 출력)
                        */}
                    {(banner.badgeText || banner.title || banner.description) && (
                      <div className="absolute inset-0 mx-auto flex h-full max-w-7xl flex-col justify-center px-4 lg:px-6 pointer-events-none">
                          <div className="relative z-10 max-w-xl text-white drop-shadow-md">
                              {banner.badgeText && (
                                <span className="inline-flex rounded-full bg-slate-900/50 px-3 py-1 text-sm font-bold backdrop-blur mb-4">
                                            {banner.badgeText}
                                        </span>
                              )}
                              <h1 className="text-3xl font-bold leading-tight lg:text-5xl whitespace-pre-line text-white shadow-black/20 text-shadow">
                                  {banner.title}
                              </h1>
                              {banner.description && (
                                <p className="mt-4 text-sm leading-6 text-slate-100 lg:text-base whitespace-pre-line">
                                    {banner.description}
                                </p>
                              )}
                          </div>
                      </div>
                    )}

                    {/* 배너 전체를 클릭했을 때 이동할 링크가 있다면 덮어씌움 */}
                    {banner.linkUrl && (
                      <Link to={banner.linkUrl} className="absolute inset-0 z-0" aria-label="이벤트 바로가기" />
                    )}
                </div>
              ))}
          </div>

          {/* 좌우 슬라이드 이동 화살표 (마우스 호버 시에만 나타남) */}
          {banners.length > 1 && (
            <>
                <button
                  onClick={goToPrev}
                  className="absolute left-4 lg:left-12 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-black/10 text-white backdrop-blur transition-all hover:bg-black/30 opacity-0 group-hover:opacity-100 z-20"
                >
                    <ChevronLeft className="h-7 w-7 text-white" />
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-4 lg:right-12 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-black/10 text-white backdrop-blur transition-all hover:bg-black/30 opacity-0 group-hover:opacity-100 z-20"
                >
                    <ChevronRight className="h-7 w-7 text-white" />
                </button>

                {/* 하단 현재 위치 인디케이터 (동그라미) */}
                <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 gap-2 z-20">
                    {banners.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentIndex(index)}
                        className={`h-2.5 rounded-full transition-all ${
                          currentIndex === index ? "w-8 bg-slate-800" : "w-2.5 bg-slate-300 hover:bg-slate-500"
                        }`}
                      />
                    ))}
                </div>
            </>
          )}
      </div>
    )
}